<template>
  <div class="hms-page">
    <Navigation />

    <div class="container hms-page-body">
      <div class="d-flex align-items-center justify-content-between mb-3 hms-page-header">
        <div class="d-flex align-items-center gap-3">
          <a href="javascript:void(0)" @click="$router.push('/dashboard')" class="hms-back" title="Back">
            <i class="mdi mdi-arrow-left"></i>
          </a>
          <h4 class="hms-page-title m-0"><i class="mdi mdi-bell-outline me-2"></i>{{ $t('page.notifications', 'Notifications — SMS & Email') }}</h4>
        </div>
        <div class="d-flex gap-2">
          <button class="hms-btn hms-btn-warning" @click="processQueue" :disabled="processing"><i class="mdi mdi-send"></i> {{ processing?'Processing…':'Process queue' }}</button>
          <button class="hms-btn hms-btn-success" @click="openSend()"><i class="mdi mdi-plus"></i> New message</button>
          <button class="hms-btn hms-btn-blue" @click="loadNotifications"><i class="mdi mdi-refresh"></i></button>
        </div>
      </div>

      <!-- KPI -->
      <div class="hms-stat-row">
        <div class="hms-stat"><div class="hms-stat-num hms-stat-success">{{ stats.sent_today || 0 }}</div><div class="hms-stat-lbl">Sent today</div></div>
        <div class="hms-stat"><div class="hms-stat-num hms-stat-warning">{{ stats.queued || 0 }}</div><div class="hms-stat-lbl">Queued</div></div>
        <div class="hms-stat"><div class="hms-stat-num hms-stat-danger">{{ stats.failed || 0 }}</div><div class="hms-stat-lbl">Failed</div></div>
        <div class="hms-stat"><div class="hms-stat-num hms-stat-info">{{ stats.sms_30d || 0 }}</div><div class="hms-stat-lbl">SMS (30d)</div></div>
        <div class="hms-stat"><div class="hms-stat-num hms-stat-info">{{ stats.email_30d || 0 }}</div><div class="hms-stat-lbl">Email (30d)</div></div>
        <div class="hms-stat"><div class="hms-stat-num">{{ stats.opted_out || 0 }}</div><div class="hms-stat-lbl">Opted-out</div></div>
        <div class="hms-stat"><div class="hms-stat-num">KES {{ formatMoney(stats.total_cost) }}</div><div class="hms-stat-lbl">Total cost</div></div>
      </div>

      <!-- TABS -->
      <div class="hms-tab-row">
        <button class="hms-tab" :class="{ active: tab==='outbox' }" @click="tab='outbox'"><i class="mdi mdi-inbox-arrow-up me-1"></i>Outbox</button>
        <button class="hms-tab" :class="{ active: tab==='templates' }" @click="tab='templates'; loadTemplates()"><i class="mdi mdi-file-document-edit me-1"></i>Templates</button>
        <button class="hms-tab" :class="{ active: tab==='bulk' }" @click="tab='bulk'; loadTemplates()"><i class="mdi mdi-broadcast me-1"></i>Bulk send</button>
        <button class="hms-tab" :class="{ active: tab==='settings' }" @click="tab='settings'; loadSettings()"><i class="mdi mdi-cog me-1"></i>Settings</button>
        <button class="hms-tab" :class="{ active: tab==='optout' }" @click="tab='optout'; loadOptOuts()"><i class="mdi mdi-cancel me-1"></i>Opt-out list</button>
      </div>

      <!-- ===== OUTBOX ===== -->
      <div v-if="tab==='outbox'">
        <div class="d-flex gap-2 mb-2">
          <select v-model="filterStatus" class="hms-input" @change="loadNotifications" style="max-width:160px">
            <option value="">All statuses</option>
            <option value="queued">Queued</option><option value="sent">Sent</option>
            <option value="delivered">Delivered</option><option value="failed">Failed</option>
            <option value="opted_out">Opted out</option>
          </select>
          <select v-model="filterChannel" class="hms-input" @change="loadNotifications" style="max-width:140px">
            <option value="">All channels</option>
            <option value="sms">SMS</option><option value="email">Email</option>
          </select>
        </div>

        <table class="hms-table">
          <thead><tr><th>#</th><th>Channel</th><th>Recipient</th><th>Category</th><th>Body</th><th>Status</th><th>When</th><th>Cost</th></tr></thead>
          <tbody>
            <tr v-for="n in notifications" :key="n.notification_id" :class="rowClass(n)">
              <td>{{ n.notification_id }}</td>
              <td><span class="hms-pill" :class="channelPill(n.channel)"><i class="mdi" :class="n.channel==='sms'?'mdi-message-text':'mdi-email'"></i> {{ n.channel }}</span></td>
              <td>{{ n.recipient_name || '—' }}<div class="hms-muted-sm">{{ n.recipient_phone || n.recipient_email }}</div></td>
              <td><span class="hms-pill hms-pill-muted">{{ n.category }}</span></td>
              <td class="hms-body-cell">{{ n.message_body }}</td>
              <td>
                <span class="hms-pill" :class="statusPill(n.status)">{{ n.status }}</span>
                <div v-if="n.last_error" class="hms-muted-sm st-bad">{{ n.last_error }}</div>
              </td>
              <td class="hms-muted-sm">{{ formatDt(n.sent_at || n.created_at) }}<div v-if="n.attempts > 0" class="hms-muted-sm">attempts: {{ n.attempts }}</div></td>
              <td>{{ n.provider_cost || '—' }}</td>
            </tr>
            <tr v-if="!notifications.length"><td colspan="8" class="text-center hms-pad hms-muted">No notifications.</td></tr>
          </tbody>
        </table>
      </div>

      <!-- ===== TEMPLATES ===== -->
      <div v-if="tab==='templates'">
        <div class="d-flex justify-content-end mb-2">
          <button class="hms-btn hms-btn-success" @click="openTplModal()"><i class="mdi mdi-plus"></i> New template</button>
        </div>
        <table class="hms-table">
          <thead><tr><th>Code</th><th>Name</th><th>Channel</th><th>Category</th><th>Body</th><th>Active</th><th></th></tr></thead>
          <tbody>
            <tr v-for="t in templates" :key="t.template_id">
              <td><code>{{ t.template_code }}</code> <span v-if="t.is_system" class="hms-pill hms-pill-info">system</span></td>
              <td>{{ t.template_name }}</td>
              <td><span class="hms-pill" :class="channelPill(t.channel)">{{ t.channel }}</span></td>
              <td><span class="hms-pill hms-pill-muted">{{ t.category }}</span></td>
              <td class="hms-body-cell">{{ t.body }}</td>
              <td><span :class="t.is_active==1 ? 'st-good' : 'st-bad'">{{ t.is_active==1 ? 'YES' : 'NO' }}</span></td>
              <td><button class="hms-btn-link" @click="openTplModal(t)"><i class="mdi mdi-pencil"></i></button></td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- ===== BULK ===== -->
      <div v-if="tab==='bulk'">
        <div class="hms-card" style="max-width:720px">
          <div class="hms-card-head">Bulk send (campaign)</div>
          <div class="hms-card-body">
            <div class="row g-2">
              <div class="col-12"><label class="hms-label">Campaign name *</label><input v-model="bulk.campaign_name" class="hms-input" placeholder="e.g. Polio campaign reminder — May 2026" /></div>
              <div class="col-4"><label class="hms-label">Channel</label>
                <select v-model="bulk.channel" class="hms-input"><option value="sms">SMS</option><option value="email">Email</option></select>
              </div>
              <div class="col-8"><label class="hms-label">Template *</label>
                <select v-model.number="bulk.template_id" class="hms-input">
                  <option :value="null">— select —</option>
                  <option v-for="t in templatesByChannel" :key="t.template_id" :value="t.template_id">{{ t.template_code }} — {{ t.template_name }}</option>
                </select>
              </div>
              <div class="col-12"><label class="hms-label">Audience</label>
                <select v-model="bulk.audience" class="hms-input">
                  <option value="all_patients">All patients with valid contact</option>
                  <option value="recent_visits">Patients with visits in last 30 days</option>
                </select>
              </div>
              <div class="col-12"><label class="hms-label">Variables (JSON, optional)</label><textarea v-model="bulk.variables" class="hms-input" rows="3" placeholder='{"facility":"Narok L5","date":"2026-05-15"}'></textarea></div>
            </div>
            <div class="text-end mt-3">
              <button class="hms-btn hms-btn-success" :disabled="bulkSaving || !bulk.campaign_name || !bulk.template_id" @click="runBulk">{{ bulkSaving?'Queueing…':'Queue campaign' }}</button>
            </div>
          </div>
        </div>
      </div>

      <!-- ===== SETTINGS ===== -->
      <div v-if="tab==='settings'">
        <div class="hms-card" style="max-width:840px">
          <div class="hms-card-head">Provider configuration</div>
          <div class="hms-card-body">
            <div class="hms-section-lbl">Africa's Talking SMS</div>
            <div class="row g-2">
              <div class="col-4"><label class="hms-label">Username</label><input v-model="settings.sms_username" class="hms-input" /></div>
              <div class="col-5"><label class="hms-label">API key</label><input v-model="settings.sms_api_key" type="password" class="hms-input" /></div>
              <div class="col-3"><label class="hms-label">Sender ID</label><input v-model="settings.sms_sender_id" class="hms-input" placeholder="optional" /></div>
              <div class="col-12"><label><input type="checkbox" v-model="settings.sms_sandbox" :true-value="1" :false-value="0" /> Use sandbox</label></div>
            </div>

            <div class="hms-section-lbl mt-3">SMTP Email</div>
            <div class="row g-2">
              <div class="col-7"><label class="hms-label">Host</label><input v-model="settings.smtp_host" class="hms-input" /></div>
              <div class="col-2"><label class="hms-label">Port</label><input v-model.number="settings.smtp_port" type="number" class="hms-input" /></div>
              <div class="col-3"><label class="hms-label">Security</label>
                <select v-model="settings.smtp_secure" class="hms-input"><option value="none">none</option><option value="tls">TLS</option><option value="ssl">SSL</option></select>
              </div>
              <div class="col-6"><label class="hms-label">Username</label><input v-model="settings.smtp_username" class="hms-input" /></div>
              <div class="col-6"><label class="hms-label">Password</label><input v-model="settings.smtp_password" type="password" class="hms-input" /></div>
              <div class="col-6"><label class="hms-label">From email</label><input v-model="settings.smtp_from_email" class="hms-input" /></div>
              <div class="col-6"><label class="hms-label">From name</label><input v-model="settings.smtp_from_name" class="hms-input" /></div>
            </div>

            <div class="hms-section-lbl mt-3">Throttling</div>
            <div class="row g-2">
              <div class="col-3"><label class="hms-label">Quiet start</label><input v-model="settings.quiet_hours_start" type="time" class="hms-input" /></div>
              <div class="col-3"><label class="hms-label">Quiet end</label><input v-model="settings.quiet_hours_end" type="time" class="hms-input" /></div>
              <div class="col-3"><label class="hms-label">Daily cap</label><input v-model.number="settings.daily_cap" type="number" class="hms-input" placeholder="0 = unlimited" /></div>
              <div class="col-3 d-flex align-items-end"><label><input type="checkbox" v-model="settings.is_active" :true-value="1" :false-value="0" /> Active</label></div>
            </div>

            <div class="text-end mt-3">
              <button class="hms-btn hms-btn-success" :disabled="settingsSaving" @click="saveSettings">{{ settingsSaving?'Saving…':'Save settings' }}</button>
            </div>
          </div>
        </div>
      </div>

      <!-- ===== OPT-OUT ===== -->
      <div v-if="tab==='optout'">
        <table class="hms-table">
          <thead><tr><th>Contact</th><th>Type</th><th>Patient</th><th>Reason</th><th>Opted out</th><th></th></tr></thead>
          <tbody>
            <tr v-for="o in optOuts" :key="o.optout_id">
              <td><strong>{{ o.contact_value }}</strong></td>
              <td><span class="hms-pill" :class="o.contact_type==='phone'?'hms-pill-info':'hms-pill-warn'">{{ o.contact_type }}</span></td>
              <td>{{ o.patient_id || '—' }}</td>
              <td>{{ o.reason || '—' }}</td>
              <td class="hms-muted-sm">{{ formatDt(o.opted_out_at) }}</td>
              <td><button class="hms-btn-link" @click="removeOptOut(o)"><i class="mdi mdi-trash-can"></i></button></td>
            </tr>
            <tr v-if="!optOuts.length"><td colspan="6" class="text-center hms-pad hms-muted">No opt-outs.</td></tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- ===== SEND MODAL ===== -->
    <div v-if="sendModal" class="hms-modal-bg" @click.self="sendModal=false">
      <div class="hms-modal" style="width:680px">
        <div class="hms-modal-head"><h5>Send message</h5><button class="hms-icon-btn" @click="sendModal=false"><i class="mdi mdi-close"></i></button></div>
        <div class="hms-modal-body">
          <div class="row g-2">
            <div class="col-3"><label class="hms-label">Channel</label>
              <select v-model="form.channel" class="hms-input"><option value="sms">SMS</option><option value="email">Email</option></select>
            </div>
            <div class="col-3"><label class="hms-label">Priority</label>
              <select v-model="form.priority" class="hms-input"><option value="low">Low</option><option value="normal">Normal</option><option value="high">High</option><option value="urgent">Urgent</option></select>
            </div>
            <div class="col-6"><label class="hms-label">Template (optional)</label>
              <select v-model.number="form.template_id" @change="onTplPick" class="hms-input">
                <option :value="null">— custom message —</option>
                <option v-for="t in templatesByChannel" :key="t.template_id" :value="t.template_id">{{ t.template_code }}</option>
              </select>
            </div>
            <div class="col-3"><label class="hms-label">Patient ID</label><input v-model.number="form.patient_id" type="number" class="hms-input" /></div>
            <div class="col-5"><label class="hms-label">Recipient name</label><input v-model="form.recipient_name" class="hms-input" /></div>
            <div v-if="form.channel === 'sms'" class="col-4"><label class="hms-label">Phone *</label><input v-model="form.recipient_phone" class="hms-input" placeholder="+254…" /></div>
            <div v-if="form.channel === 'email'" class="col-4"><label class="hms-label">Email *</label><input v-model="form.recipient_email" class="hms-input" /></div>
            <div v-if="form.channel === 'email'" class="col-12"><label class="hms-label">Subject</label><input v-model="form.subject" class="hms-input" /></div>
            <div class="col-12"><label class="hms-label">Message *</label><textarea v-model="form.message_body" class="hms-input" rows="5"></textarea>
              <div class="hms-muted-sm">Length: {{ (form.message_body || '').length }} chars{{ form.channel==='sms' ? ' · ' + Math.ceil((form.message_body||'').length / 160) + ' SMS part(s)' : '' }}</div>
            </div>
            <div class="col-6"><label class="hms-label">Schedule for (optional)</label><input v-model="form.scheduled_at" type="datetime-local" class="hms-input" /></div>
            <div class="col-6"><label class="hms-label">Variables (JSON)</label><input v-model="form.variables" class="hms-input" placeholder='{"date":"2026-05-15"}' /></div>
          </div>
        </div>
        <div class="hms-modal-foot">
          <button class="hms-btn hms-btn-secondary" @click="sendModal=false">Cancel</button>
          <button class="hms-btn hms-btn-secondary" :disabled="saving" @click="send(false)">Queue</button>
          <button class="hms-btn hms-btn-success" :disabled="saving" @click="send(true)">{{ saving?'Sending…':'Send now' }}</button>
        </div>
      </div>
    </div>

    <!-- ===== TEMPLATE MODAL ===== -->
    <div v-if="tplModal" class="hms-modal-bg" @click.self="tplModal=false">
      <div class="hms-modal" style="width:680px">
        <div class="hms-modal-head"><h5>{{ tplForm.template_id ? 'Edit template' : 'New template' }}</h5><button class="hms-icon-btn" @click="tplModal=false"><i class="mdi mdi-close"></i></button></div>
        <div class="hms-modal-body">
          <div class="row g-2">
            <div class="col-4"><label class="hms-label">Code *</label><input v-model="tplForm.template_code" class="hms-input" placeholder="UPPERCASE_SNAKE" /></div>
            <div class="col-4"><label class="hms-label">Channel</label>
              <select v-model="tplForm.channel" class="hms-input"><option value="sms">SMS</option><option value="email">Email</option><option value="both">Both</option></select>
            </div>
            <div class="col-4"><label class="hms-label">Category</label>
              <select v-model="tplForm.category" class="hms-input">
                <option value="general">General</option><option value="appointment">Appointment</option>
                <option value="results">Results</option><option value="immunization">Immunization</option>
                <option value="otp">OTP</option><option value="billing">Billing</option>
                <option value="telehealth">Telehealth</option><option value="referral">Referral</option>
                <option value="maternity">Maternity</option><option value="campaign">Campaign</option>
              </select>
            </div>
            <div class="col-12"><label class="hms-label">Name *</label><input v-model="tplForm.template_name" class="hms-input" /></div>
            <div v-if="tplForm.channel !== 'sms'" class="col-12"><label class="hms-label">Subject</label><input v-model="tplForm.subject" class="hms-input" /></div>
            <div class="col-12"><label class="hms-label">Body *</label><textarea v-model="tplForm.body" class="hms-input" rows="6"></textarea>
              <div class="hms-muted-sm">Use <code v-pre>{{patient_name}}</code>, <code v-pre>{{date}}</code> etc. for placeholders.</div>
            </div>
            <div class="col-6 d-flex align-items-end"><label><input type="checkbox" v-model="tplForm.is_active" :true-value="1" :false-value="0" /> Active</label></div>
          </div>
        </div>
        <div class="hms-modal-foot">
          <button class="hms-btn hms-btn-secondary" @click="tplModal=false">Cancel</button>
          <button class="hms-btn hms-btn-success" :disabled="saving" @click="saveTemplate">{{ saving?'Saving…':'Save' }}</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Navigation from "@/components/hms-auth/Navigation.vue";
import { executeJson, hms } from "@/api";

export default {
  name: "NotificationsCentre",
  components: { Navigation },
  data() {
    return {
      tab: 'outbox', saving: false, processing: false, settingsSaving: false, bulkSaving: false,
      filterStatus: '', filterChannel: '',
      notifications: [], stats: {},
      templates: [],
      optOuts: [],
      settings: { sms_sandbox: 1, smtp_secure: 'tls', is_active: 1 },
      sendModal: false, form: this.blankForm(),
      tplModal: false, tplForm: this.blankTpl(),
      bulk: { campaign_name: '', channel: 'sms', template_id: null, audience: 'all_patients', variables: '' }
    };
  },
  computed: {
    templatesByChannel() {
      const ch = this.tab === 'bulk' ? this.bulk.channel : this.form.channel;
      return this.templates.filter(t => t.is_active == 1 && (t.channel === ch || t.channel === 'both'));
    }
  },
  mounted() { this.loadNotifications(); },
  methods: {
    blankForm() { return { channel: 'sms', priority: 'normal', template_id: null, patient_id: null, recipient_name: '', recipient_phone: '', recipient_email: '', subject: '', message_body: '', scheduled_at: '', variables: '' }; },
    blankTpl() { return { template_id: null, template_code: '', template_name: '', channel: 'sms', category: 'general', subject: '', body: '', is_active: 1 }; },

    async loadNotifications() {
      try {
        const r = await executeJson({ function: 'getNotifications', status: this.filterStatus, channel: this.filterChannel }, hms);
        if (r.data && r.data.success) {
          this.notifications = r.data.data.Notifications || [];
          this.stats = r.data.data.Stats || {};
        }
      } catch (e) { console.error(e); }
    },
    async loadTemplates() {
      const r = await executeJson({ function: 'getNotificationTemplates' }, hms);
      if (r.data && r.data.success) this.templates = r.data.data.Templates || [];
    },
    async loadSettings() {
      const r = await executeJson({ function: 'getNotificationSettings' }, hms);
      if (r.data && r.data.success) this.settings = r.data.data.Settings || { sms_sandbox: 1, smtp_secure: 'tls', is_active: 1 };
    },
    async loadOptOuts() {
      const r = await executeJson({ function: 'getOptOutList' }, hms);
      if (r.data && r.data.success) this.optOuts = r.data.data.OptOuts || [];
    },

    openSend(prefill) {
      this.form = { ...this.blankForm(), ...(prefill || {}) };
      if (!this.templates.length) this.loadTemplates();
      this.sendModal = true;
    },
    onTplPick() {
      const t = this.templates.find(x => x.template_id === this.form.template_id);
      if (t) {
        this.form.message_body = t.body;
        if (t.subject) this.form.subject = t.subject;
        this.form.channel = t.channel === 'both' ? this.form.channel : t.channel;
      }
    },
    async send(now) {
      this.saving = true;
      try {
        const r = await executeJson({ function: 'sendNotification', ...this.form, send_now: now ? 1 : 0 }, hms);
        if (r.data && r.data.success) {
          this.sendModal = false;
          await this.loadNotifications();
          alert(r.data.message || 'OK');
        } else alert((r.data && r.data.message) || 'Failed.');
      } catch (e) { console.error(e); }
      finally { this.saving = false; }
    },

    async processQueue() {
      this.processing = true;
      try {
        const r = await executeJson({ function: 'processNotificationQueue', limit: 50 }, hms);
        if (r.data && r.data.success) {
          await this.loadNotifications();
          alert(r.data.message || 'OK');
        } else alert((r.data && r.data.message) || 'Failed.');
      } catch (e) { console.error(e); }
      finally { this.processing = false; }
    },

    openTplModal(t) { this.tplForm = t ? { ...this.blankTpl(), ...t } : this.blankTpl(); this.tplModal = true; },
    async saveTemplate() {
      this.saving = true;
      try {
        const r = await executeJson({ function: 'saveNotificationTemplate', ...this.tplForm }, hms);
        if (r.data && r.data.success) { this.tplModal = false; await this.loadTemplates(); }
        else alert((r.data && r.data.message) || 'Failed.');
      } catch (e) { console.error(e); }
      finally { this.saving = false; }
    },

    async saveSettings() {
      this.settingsSaving = true;
      try {
        const r = await executeJson({ function: 'saveNotificationSettings', ...this.settings }, hms);
        if (r.data && r.data.success) { await this.loadSettings(); alert('Saved.'); }
      } catch (e) { console.error(e); }
      finally { this.settingsSaving = false; }
    },

    async runBulk() {
      this.bulkSaving = true;
      try {
        const r = await executeJson({ function: 'sendBulkNotification', ...this.bulk }, hms);
        if (r.data && r.data.success) {
          alert(r.data.message || 'OK');
          this.bulk = { campaign_name: '', channel: 'sms', template_id: null, audience: 'all_patients', variables: '' };
          await this.loadNotifications();
        } else alert((r.data && r.data.message) || 'Failed.');
      } catch (e) { console.error(e); }
      finally { this.bulkSaving = false; }
    },

    async removeOptOut(o) {
      if (!confirm('Remove this opt-out?')) return;
      const r = await executeJson({ function: 'removeOptOut', optout_id: o.optout_id }, hms);
      if (r.data && r.data.success) await this.loadOptOuts();
    },

    rowClass(n) {
      if (n.status === 'failed') return 'row-action';
      if (n.status === 'queued') return 'row-warn';
      if (n.status === 'sent' || n.status === 'delivered') return 'row-good';
      return '';
    },
    statusPill(s) { return ({ queued:'hms-pill-warn', sending:'hms-pill-info', sent:'hms-pill-success', delivered:'hms-pill-success', failed:'hms-pill-danger', cancelled:'hms-pill-muted', opted_out:'hms-pill-danger' })[s] || ''; },
    channelPill(c) { return ({ sms:'hms-pill-info', email:'hms-pill-warn' })[c] || ''; },
    formatDt(d) { if (!d) return ''; return new Date(d.replace(' ','T')).toLocaleString(); },
    formatMoney(v) { const n = Number(v||0); return n.toLocaleString(undefined, { maximumFractionDigits: 2 }); }
  }
};
</script>

<style scoped>
.hms-page-body { padding: 16px 0 32px; }
.hms-back { color: #4a5568; font-size: 20px; text-decoration: none; }
.hms-page-title { font-size: 18px; font-weight: 600; color: #2d3748; }

.hms-tab-row { display: flex; gap: 4px; border-bottom: 1px solid #e2e8f0; margin-bottom: 14px; }
.hms-tab { padding: 10px 16px; border: none; background: none; cursor: pointer; color: #718096; border-bottom: 2px solid transparent; font-size: 14px; }
.hms-tab.active { color: #2b6cb0; border-bottom-color: #2b6cb0; font-weight: 600; }

.hms-stat-row { display: grid; grid-template-columns: repeat(7, 1fr); gap: 10px; margin-bottom: 14px; }
.hms-stat { background: #fff; border: 1px solid #e2e8f0; border-radius: 8px; padding: 12px; text-align: center; }
.hms-stat-num { font-size: 22px; font-weight: 700; color: #1a202c; }
.hms-stat-lbl { font-size: 11px; color: #718096; text-transform: uppercase; letter-spacing: .4px; margin-top: 2px; }
.hms-stat-info { color: #2c5282; } .hms-stat-success { color: #276749; } .hms-stat-warning { color: #c05621; } .hms-stat-danger { color: #c53030; }

.hms-table { width: 100%; border-collapse: collapse; font-size: 13px; background: #fff; }
.hms-table th { background: #f7fafc; padding: 9px; border-bottom: 1px solid #e2e8f0; font-weight: 600; color: #4a5568; text-align: left; }
.hms-table td { padding: 8px 9px; border-bottom: 1px solid #edf2f7; vertical-align: middle; }
.hms-table tr.row-warn   { background: #fffaf0; }
.hms-table tr.row-action { background: #fff5f5; }
.hms-table tr.row-good   { background: #f0fff4; }

.hms-body-cell { max-width: 360px; white-space: pre-wrap; word-break: break-word; font-size: 12px; color: #4a5568; }

.hms-input { width: 100%; padding: 6px 10px; border: 1px solid #cbd5e0; border-radius: 6px; font-size: 13px; }
.hms-label { font-size: 11px; font-weight: 600; color: #4a5568; display: block; margin-bottom: 3px; }
.hms-section-lbl { font-size: 11px; font-weight: 700; color: #718096; text-transform: uppercase; letter-spacing: .5px; padding-bottom: 3px; border-bottom: 1px solid #e2e8f0; }

.hms-pill { display: inline-flex; align-items: center; gap: 3px; padding: 2px 8px; border-radius: 10px; font-size: 11px; font-weight: 600; }
.hms-pill-success { background: #c6f6d5; color: #22543d; }
.hms-pill-info    { background: #bee3f8; color: #2a4365; }
.hms-pill-warn    { background: #feebc8; color: #7b341e; }
.hms-pill-danger  { background: #fed7d7; color: #742a2a; }
.hms-pill-muted   { background: #edf2f7; color: #4a5568; }

.hms-modal-bg { position: fixed; inset: 0; background: rgba(0,0,0,.55); display: flex; align-items: center; justify-content: center; z-index: 100; padding: 20px; }
.hms-modal { background: #fff; border-radius: 8px; max-height: 95vh; display: flex; flex-direction: column; overflow: hidden; }
.hms-modal-head { padding: 14px 18px; border-bottom: 1px solid #e2e8f0; display: flex; justify-content: space-between; align-items: center; }
.hms-modal-body { padding: 16px 18px; overflow-y: auto; }
.hms-modal-foot { padding: 12px 18px; border-top: 1px solid #e2e8f0; display: flex; justify-content: flex-end; gap: 8px; }
.hms-icon-btn { background: none; border: none; cursor: pointer; }

.hms-card { background: #fff; border: 1px solid #e2e8f0; border-radius: 6px; }
.hms-card-head { padding: 8px 12px; border-bottom: 1px solid #e2e8f0; font-weight: 600; color: #2d3748; font-size: 13px; }
.hms-card-body { padding: 14px; }

.hms-btn { display: inline-flex; align-items: center; gap: 4px; padding: 7px 12px; border-radius: 6px; border: 1px solid transparent; font-size: 13px; font-weight: 500; cursor: pointer; }
.hms-btn-blue      { background: #2b6cb0; color: #fff; }
.hms-btn-success   { background: #38a169; color: #fff; }
.hms-btn-warning   { background: #d69e2e; color: #fff; }
.hms-btn-secondary { background: #fff; color: #2d3748; border: 1px solid #cbd5e0; }
.hms-btn-link      { background: transparent; color: #2b6cb0; border: none; cursor: pointer; }

.st-bad { color: #c53030; font-weight: 600; }
.st-good { color: #276749; font-weight: 600; }
.hms-muted { color: #a0aec0; }
.hms-muted-sm { color: #a0aec0; font-size: 11px; }
.hms-pad { padding: 24px; }
</style>
